import React, { useCallback, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GeneratedDate, forcedDatesOf, includedHolidaysOf } from '../../../../../utils/recurrence';
import CustomButton from '../../../../../components/CustomButton';
import CustomModal from '../../../../common/CustomModal';
import { createSeries } from '../../../../../flows/seriesModuleFlow';
import {
    CreateSeriesResponse,
    SERIES_PAYMENT_TERM_SELECTIONS,
    SeriesPaymentTermSelection,
    SeriesRequestPayload,
    selectionToSeriesPaymentTerm,
} from '../../../../../types/bookingCreation';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { paths } from '../../../../../routes';
import type { SeriesBookingData } from './BookingDataForm';
import { singleDatePattern } from './seriesRequest';
import { CUSTOMER_MAIL_FIX, CustomerMailRefusal, mailBlockedOf } from '../../../../../utils/customerMailGuard';

interface BatchCreationProgressProps {
    dates: GeneratedDate[];
    excludedDates?: GeneratedDate[];
    bookingData: SeriesBookingData;
    /** The preview's request — the create route takes the same body. */
    seriesRequest: SeriesRequestPayload;
    onCreationStateChange?: (state: 'idle' | 'creating' | 'complete') => void;
}

type EmailSendStatus = 'idle' | 'sending' | 'sent' | 'failed' | 'skipped' | 'blocked';

const ISO_DATE = 'YYYY-MM-DD';

/** The mail line after a create: no series created means no mail to send. */
const emailStatusAfter = (withMail: boolean, response: CreateSeriesResponse | { error: string }): EmailSendStatus => {
    if (!withMail) return 'idle';
    if ('error' in response || !response.results.some((r) => r.outcome === 'created')) return 'skipped';
    if (response.mailSent) return 'sent';
    // PO, 2026-10-30: the series is saved; the payment rule refused its mail.
    return mailBlockedOf(response) ? 'blocked' : 'failed';
};

const BatchCreationProgress: React.FC<BatchCreationProgressProps> = ({
    dates,
    excludedDates = [],
    bookingData,
    seriesRequest,
    onCreationStateChange,
}) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
    // One format for every date on this step, with the weekday in the screen's
    // language — the first/last line showed "Wed" on a German screen.
    const formatDay = (date: dayjs.Dayjs | undefined) =>
        date?.locale(lang).format(lang === 'de' ? 'dd, DD.MM.YYYY' : 'ddd, MM/DD/YYYY');
    const navigate = useNavigate();

    const [isCreating, setIsCreating] = useState(false);
    const [successCount, setSuccessCount] = useState(0);
    const [conflictCount, setConflictCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [failedDates, setFailedDates] = useState<string[]>([]);
    const [requestError, setRequestError] = useState<string | null>(null);
    const [isComplete, setIsComplete] = useState(false);
    // Assigned by the server on create; a date added afterwards joins it.
    const [seriesId, setSeriesId] = useState<string | null>(null);

    // Series-level email controls. The create route stores the payment term
    // and sends the ONE series mail itself (series plan §4.5).
    const [paymentTerm, setPaymentTerm] = useState<SeriesPaymentTermSelection>('instalments_monthly');
    const [sendMailIntent, setSendMailIntent] = useState(false);
    const [confirmSendOpen, setConfirmSendOpen] = useState(false);
    const [emailSendStatus, setEmailSendStatus] = useState<EmailSendStatus>('idle');
    const [mailRefusal, setMailRefusal] = useState<CustomerMailRefusal | null>(null);

    // Track per-excluded-date creation state
    const [excludedCreating, setExcludedCreating] = useState<Record<number, boolean>>({});
    const [excludedCreated, setExcludedCreated] = useState<Record<number, 'success' | 'failed'>>({});

    const total = dates.length;

    const handleStartCreation = useCallback(async (withMail: boolean) => {
        setSendMailIntent(withMail);
        setIsCreating(true);
        setEmailSendStatus(withMail ? 'sending' : 'idle');
        onCreationStateChange?.('creating');

        const response = await createSeries(
            {
                ...seriesRequest,
                ...selectionToSeriesPaymentTerm(paymentTerm),
                excludedDates: excludedDates.map((d) => d.date.format(ISO_DATE)),
                forcedDates: forcedDatesOf(dates),
                includedHolidays: includedHolidaysOf(dates),
            },
            withMail ? 'save_and_send_email' : 'save_only',
        );

        setEmailSendStatus(emailStatusAfter(withMail, response));
        setMailRefusal('error' in response ? null : mailBlockedOf(response));
        if ('error' in response) {
            setRequestError(response.error);
            setFailedCount(total);
        } else {
            const failed = response.results.filter((r) => r.outcome === 'failed');
            setSeriesId(response.seriesId);
            const created = response.results.filter((r) => r.outcome === 'created');
            setSuccessCount(created.length);
            setConflictCount(created.filter((r) => r.conflict).length);
            setFailedCount(failed.length);
            setFailedDates(failed.map((r) => dayjs(r.date).format('DD.MM.YYYY')));
        }

        setIsComplete(true);
        setIsCreating(false);
        onCreationStateChange?.('complete');
    }, [seriesRequest, paymentTerm, dates, excludedDates, total, onCreationStateChange]);

    // A date removed in the review, booked after all: it joins the series
    // (or starts it, when nothing was created). Never mails — the series mail
    // is the one confirmation. A taken date is created as a conflict; any other
    // refusal of the guard still fails it.
    const handleCreateExcluded = useCallback(async (index: number, dateEntry: GeneratedDate) => {
        setExcludedCreating((prev) => ({ ...prev, [index]: true }));
        const response = await createSeries(
            {
                ...seriesRequest,
                ...(seriesId ? { seriesId } : selectionToSeriesPaymentTerm(paymentTerm)),
                pattern: singleDatePattern(dateEntry.date.format(ISO_DATE)),
                excludedDates: [],
                forcedDates: forcedDatesOf([{ ...dateEntry, included: true }]),
                includedHolidays: includedHolidaysOf([{ ...dateEntry, included: true }]),
            },
            'save_only',
        );
        const createdResult = 'error' in response
            ? undefined
            : response.results.find((r) => r.outcome === 'created');
        const created = Boolean(createdResult);
        if (!('error' in response) && response.seriesId) setSeriesId(response.seriesId);
        setExcludedCreated((prev) => ({ ...prev, [index]: created ? 'success' : 'failed' }));
        if (created) setSuccessCount((prev) => prev + 1);
        if (createdResult?.conflict) setConflictCount((prev) => prev + 1);
        setExcludedCreating((prev) => ({ ...prev, [index]: false }));
    }, [seriesRequest, seriesId, paymentTerm]);

    return (
        <div className={'rounded-lg bg-white p-6 shadow-sm'}>
            <Typography variant={'h6'} gutterBottom>
                {t('series_booking.confirmation.title')}
            </Typography>

            {/* Pre-creation summary */}
            {!isCreating && !isComplete && (
                <div className={'space-y-4'}>
                    <div className={'rounded-md bg-gray-50 p-4'}>
                        <Typography variant={'body1'} gutterBottom>
                            {t('series_booking.confirmation.summary', {
                                count: total,
                                assetName: bookingData.assetName,
                            })}
                        </Typography>
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('series_booking.confirmation.customer', {
                                name: `${bookingData.firstName} ${bookingData.lastName}`,
                            })}
                        </Typography>
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('series_booking.confirmation.time_slot', {
                                startTime: dates[0]?.startTime,
                                endTime: dates[0]?.endTime,
                            })}
                        </Typography>
                        <Typography variant={'body2'} color={'text.secondary'} sx={{ mt: 1 }}>
                            {t('series_booking.confirmation.first_date', {
                                date: formatDay(dates[0]?.date),
                            })}
                            {' — '}
                            {t('series_booking.confirmation.last_date', {
                                date: formatDay(dates[dates.length - 1]?.date),
                            })}
                        </Typography>
                    </div>

                    {/* Payment frequency — persisted on the Series row in
                        the finalize step regardless of mail-send choice */}
                    <FormControl fullWidth>
                        <InputLabel id={'create-series-payment-frequency-label'}>
                            {t('series_booking_details.payment_frequency.label')}
                        </InputLabel>
                        <Select
                            labelId={'create-series-payment-frequency-label'}
                            value={paymentTerm}
                            label={t('series_booking_details.payment_frequency.label')}
                            onChange={(e) => setPaymentTerm(e.target.value as SeriesPaymentTermSelection)}
                        >
                            {SERIES_PAYMENT_TERM_SELECTIONS.map((selection) => (
                                <MenuItem key={selection} value={selection}>
                                    {t(`series_booking_details.payment_frequency.${selection}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <div className={'flex flex-col sm:flex-row justify-center gap-3'}>
                        <CustomButton
                            title={t('series_booking.confirmation.create_only', { count: total })}
                            onClick={() => handleStartCreation(false)}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t('series_booking.confirmation.create_and_send', { count: total })}
                            onClick={() => setConfirmSendOpen(true)}
                        />
                    </div>
                </div>
            )}

            {/* Creating — one request for the whole series */}
            {isCreating && (
                <div className={'space-y-4 text-center text-sm text-gray-600'}>
                    {t('series_booking.confirmation.creating')}
                    {sendMailIntent && (
                        <div className={'mt-2'}>{t('series_booking.confirmation.sending_email')}</div>
                    )}
                </div>
            )}

            {/* Results */}
            {isComplete && (
                <div className={'space-y-4'}>
                    {successCount > 0 && (
                        <div className={'rounded-md bg-green-50 p-3 text-sm text-green-800'}>
                            {t('series_booking.confirmation.success', { count: successCount })}
                            {conflictCount > 0 && (
                                <span className={'block mt-1 text-xs'}>
                                    {t('series_booking.confirmation.created_as_conflict', { count: conflictCount })}
                                </span>
                            )}
                        </div>
                    )}
                    {requestError && (
                        <div className={'rounded-md bg-red-50 p-3 text-sm text-red-800'}>
                            {t('series_booking.confirmation.request_failed', { reason: requestError })}
                        </div>
                    )}
                    {failedCount > 0 && (
                        <div className={'rounded-md bg-red-50 p-3 text-sm text-red-800'}>
                            {t('series_booking.confirmation.failed', { count: failedCount })}
                            {failedDates.length > 0 && (
                                <span className={'block mt-1 text-xs'}>
                                    {failedDates.join(', ')}
                                </span>
                            )}
                        </div>
                    )}
                    {sendMailIntent && emailSendStatus === 'sent' && (
                        <div className={'rounded-md bg-green-50 p-3 text-sm text-green-800'}>
                            {t('series_booking.confirmation.email_sent')}
                        </div>
                    )}
                    {sendMailIntent && emailSendStatus === 'failed' && (
                        <div className={'rounded-md bg-red-50 p-3 text-sm text-red-800'}>
                            {t('series_booking.confirmation.email_failed')}
                        </div>
                    )}
                    {sendMailIntent && emailSendStatus === 'blocked' && mailRefusal && (
                        <div className={'rounded-md bg-foreground/5 p-3 text-sm text-foreground'}>
                            {t('customer_mail_guard.saved_not_sent', { reason: t(CUSTOMER_MAIL_FIX[mailRefusal.reason].labelKey) })}{' '}
                            <Link to={CUSTOMER_MAIL_FIX[mailRefusal.reason].to} className={'text-primary underline'}>
                                {t('customer_mail_guard.fix_link')}
                            </Link>
                        </div>
                    )}
                    {sendMailIntent && emailSendStatus === 'skipped' && (
                        <div className={'rounded-md bg-amber-50 p-3 text-sm text-amber-800'}>
                            {t('series_booking.confirmation.email_skipped')}
                        </div>
                    )}

                    {/* Excluded / conflicting dates */}
                    {excludedDates.length > 0 && (
                        <div className={'mt-6'}>
                            <Typography variant={'subtitle1'} fontWeight={600} gutterBottom>
                                {t('series_booking.confirmation.excluded_dates_title')}
                            </Typography>
                            <div className={'rounded-md border border-amber-200 bg-amber-50'}>
                                {excludedDates.map((dateEntry, index) => {
                                    const status = excludedCreated[index];
                                    const creating = excludedCreating[index];

                                    return (
                                        <div
                                            key={dateEntry.date.toISOString()}
                                            className={
                                                'flex items-center justify-between border-b border-amber-200 ' +
                                                'px-4 py-3 last:border-b-0'
                                            }
                                        >
                                            <div className={'flex items-center gap-3'}>
                                                <span className={'text-sm font-medium'}>
                                                    {status === 'success' ? '✅' : status === 'failed' ? '❌' : '⚠️'}
                                                </span>
                                                <span className={'text-sm'}>
                                                    {formatDay(dateEntry.date)}
                                                </span>
                                                <span className={'text-sm text-gray-500'}>
                                                    {dateEntry.startTime}–{dateEntry.endTime}
                                                </span>
                                            </div>
                                            <div>
                                                {!status && !creating && (
                                                    <button
                                                        onClick={() => handleCreateExcluded(index, dateEntry)}
                                                        className={
                                                            'rounded-md bg-amber-600 px-3 py-1.5 text-xs ' +
                                                            'font-medium text-white hover:bg-amber-700'
                                                        }
                                                    >
                                                        {t('series_booking.confirmation.create_anyway')}
                                                    </button>
                                                )}
                                                {creating && (
                                                    <span className={'text-xs text-gray-500'}>
                                                        {t('series_booking.confirmation.creating')}
                                                    </span>
                                                )}
                                                {status === 'success' && (
                                                    <span className={'text-xs text-green-600'}>✓</span>
                                                )}
                                                {status === 'failed' && (
                                                    <span className={'text-xs text-red-600'}>✗</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className={'flex justify-center pt-2'}>
                        <CustomButton
                            title={t('series_booking.confirmation.go_to_bookings')}
                            onClick={() => navigate(paths.rentalBooking)}
                        />
                    </div>
                </div>
            )}

            {/* Send-mail confirmation modal */}
            <CustomModal
                open={confirmSendOpen}
                onClose={() => setConfirmSendOpen(false)}
                title={t('series_booking.confirmation.send_modal.title')}
                description={t('series_booking.confirmation.send_modal.description', {
                    eligible: total,
                    totalCount: total + excludedDates.length,
                })}
                saveButton={
                    <CustomButton
                        title={t('series_booking.confirmation.send_modal.confirm_button')}
                        onClick={() => {
                            setConfirmSendOpen(false);
                            handleStartCreation(true);
                        }}
                    />
                }
                closeButton={
                    <CustomButton
                        title={t('series_booking.confirmation.send_modal.cancel_button')}
                        onClick={() => setConfirmSendOpen(false)}
                        isFilled={false}
                    />
                }
            />
        </div>
    );
};

export default BatchCreationProgress;
